import { useI18n } from "vue-i18n";
import { formatDate } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { useDedupAccessibleName } from "@/shared/composables/useDedupAccessibleName";
/**
 * Accessible-name strings for the contact duplicate-detection result cards
 * (`ContactDedupPanel`). Structurally follows the reception module's
 * `useCandidateAccessibleName` — the disambiguating detail is the birth date, same as
 * reception, since that is the identity fragment available on a contact candidate. The
 * shared drop-cleanly-when-absent plumbing lives in `useDedupAccessibleName`; this composable
 * only owns the contact i18n namespace and the birth-date formatting.
 *
 * Unlike reception's own `*AriaLabel` keys (which only require the visible label to be
 * CONTAINED — the WCAG 2.5.3 minimum, letting nl put the verb last), this ticket's AC sets a
 * stricter bar: the accessible name must START WITH the button's visible label — so
 * `previewAriaLabel`/`viewRecordAriaLabel` MUST stay verb-first in every locale, including nl.
 */
const A11Y_PREFIX = "contact.create.dedup";
export const CONTACT_DEDUP_A11Y_KEYS = {
    preview: `${A11Y_PREFIX}.previewAriaLabel`,
    viewRecord: `${A11Y_PREFIX}.viewRecordAriaLabel`,
    identityWithBirthDate: `${A11Y_PREFIX}.identityWithBirthDate`,
};
export function useContactDedupAccessibleName() {
    const { t, locale } = useI18n();
    const profileStore = useProfileStore();
    const { dedupResultAccessibleName: buildAccessibleName } = useDedupAccessibleName();
    function dedupResultAccessibleName(actionKey, name, birthDate) {
        return buildAccessibleName(actionKey, name, birthDate, (candidateName, date) => t(CONTACT_DEDUP_A11Y_KEYS.identityWithBirthDate, {
            name: candidateName,
            birthDate: formatDate(date, locale.value, profileStore.profile?.dateFormat),
        }));
    }
    return { dedupResultAccessibleName };
}
